import React, { useState } from 'react';

import { useSnackbar } from 'notistack';
import pluralize from 'pluralize';

import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import StarsOutlinedIcon from '@mui/icons-material/StarsOutlined';
import { Box, Button, CircularProgress, Divider, Paper, Stack, Typography, alpha } from '@mui/material';

import { LUX_LOYALTY_PROGRAM_CONFIG } from '@luxuryescapes/lib-lux-loyalty';

import CopyableField from '~/components/Common/CopyableField';

import { ROLE_LUX_LOYALTY_ADMIN } from '~/consts/roles';

import { useResendInfiniteOnboardingEmail } from '~/hooks/apis/loyalty/useCustomerServiceRepAssignments';
import useCurrentUser from '~/hooks/useCurrentUser';

import { formatDateSlashes, formatDateSlashesWithClock } from '~/services/TimeService';

import isProd from '~/utils/isProd';
import { getLuxLoyaltyTierDisplayName } from '~/utils/luxLoyalty';
import { formatDecimal } from '~/utils/numberFormatters';
import { titleCase } from '~/utils/stringUtils';

import LuxLoyaltyAdjustPointsModal from './LuxLoyaltyAdjustPointsModal';
import LuxLoyaltyChangeRepDialog from './LuxLoyaltyChangeRepDialog';
import LuxLoyaltyChangeTierModal from './LuxLoyaltyChangeTierModal';
import RegionSwitcher from './RegionSwitcher';

const UPGRADEABLE_TIERS: Array<App.LuxLoyaltyTier> = ['bronze', 'silver', 'gold', 'platinum'];

const sectionActionButtonSx = {
  borderRadius: 999,
  minHeight: 32,
  px: 2,
  py: 0.5,
  textTransform: 'none',
  fontWeight: 700,
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  '&:hover': { bgcolor: 'primary.dark' },
} as const;

interface Props {
  account: App.LuxLoyaltyAccount;
  onAccountUpdated?: () => void;
}

interface KpiTileProps {
  label: string;
  value: React.ReactNode;
  accentTone?: 'primary' | 'neutral';
}

/**
 * Compact KPI tile used in the metrics strip. Defined as a render helper
 * (not a named component) so the eslint "one React component per file" rule
 * stays happy.
 */
function renderKpiTile({ label, value, accentTone = 'neutral' }: KpiTileProps) {
  return (
    <Paper
      key={label}
      elevation={0}
      sx={(theme) => ({
        flex: 1,
        minWidth: 0,
        p: 2,
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        borderLeft: 3,
        borderLeftColor: accentTone === 'primary' ? theme.palette.primary.main : theme.palette.divider,
        bgcolor: 'background.paper',
      })}
    >
      <Typography
        variant="overline"
        sx={{ letterSpacing: 0.8, color: 'text.secondary', display: 'block', lineHeight: 1.4 }}
      >
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2, mt: 0.5 }}>
        {value}
      </Typography>
    </Paper>
  );
}

export default function LuxLoyaltyAccountOverview({ account, onAccountUpdated }: Props) {
  const formattedStatusCreditsName = titleCase(pluralize(LUX_LOYALTY_PROGRAM_CONFIG.statusCreditsUnit));
  const formattedCurrencyName = titleCase(pluralize(LUX_LOYALTY_PROGRAM_CONFIG.pointsUnit));
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useCurrentUser();
  const [changeTierModalOpen, setChangeTierModalOpen] = useState(false);
  const canChangeTier = !isProd() || !!user?.roles.includes(ROLE_LUX_LOYALTY_ADMIN);
  const [adjustPointsModalOpen, setAdjustPointsModalOpen] = useState(false);
  const canAdjustPoints = !isProd() || !!user?.roles.includes(ROLE_LUX_LOYALTY_ADMIN);
  const [changeRepDialogOpen, setChangeRepDialogOpen] = useState(false);

  const isInfinite = account.tier === 'infinite';
  const linkedRep = account.customerServiceRep ?? null;
  const resendInfiniteOnboardingEmailMutation = useResendInfiniteOnboardingEmail({
    onSuccess: (data) => {
      const emailStatus = data.result.sideEffects.infiniteOnboardingEmail;

      if (emailStatus.status === 'sent') {
        enqueueSnackbar('Infinite onboarding email sent', { variant: 'success' });
        return;
      }

      enqueueSnackbar(`Infinite onboarding email was not sent: ${emailStatus.reason}`, { variant: 'warning' });
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to send Infinite onboarding email: ${error.message}`, { variant: 'error' });
    },
  });

  return (
    <Stack spacing={3}>
      {/* Tier hero banner — compact */}
      <Paper
        elevation={0}
        sx={(theme) => {
          const tierPalette = theme.palette.loyaltyTiers[account.tier];
          const contrast = tierPalette?.contrastText ?? theme.palette.common.white;
          return {
            px: { xs: 2, md: 2.5 },
            py: 1.25,
            borderRadius: 2,
            color: contrast,
            background: tierPalette?.gradient ?? theme.palette.primary.main,
            boxShadow: theme.shadows[1],
            '& .MuiTypography-root': { color: contrast },
            '& .MuiSvgIcon-root': { color: contrast },
          };
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (theme) => alpha(theme.palette.common.white, 0.2),
              }}
            >
              <StarsOutlinedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography variant="overline" sx={{ letterSpacing: 1.5, opacity: 0.85 }}>
              {LUX_LOYALTY_PROGRAM_CONFIG.programDisplayName} · {account.region}
            </Typography>
            <Typography variant="h6" fontWeight={700} sx={{ textTransform: 'capitalize', lineHeight: 1.2 }}>
              {getLuxLoyaltyTierDisplayName(account.tier)}
            </Typography>
            {account.accountNumber && (
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                · Member #{account.accountNumber}
              </Typography>
            )}
          </Stack>

          {isInfinite && linkedRep && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 999,
                bgcolor: 'rgba(255,255,255,0.12)',
              }}
            >
              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                Rep
              </Typography>
              <Typography variant="caption" fontWeight={700}>
                {linkedRep.firstName} {linkedRep.lastName}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                · {linkedRep.email}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* KPI tiles */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        {renderKpiTile({
          label: formattedCurrencyName,
          value: formatDecimal(account.points),
          accentTone: 'primary',
        })}
        {renderKpiTile({
          label: formattedStatusCreditsName,
          value: formatDecimal(account.statusCredits),
        })}
        {renderKpiTile({ label: 'Number of Nights', value: account.numberOfNights })}
      </Stack>

      {canAdjustPoints && (
        <Box>
          <Button
            variant="contained"
            disableElevation
            onClick={() => setAdjustPointsModalOpen(true)}
            sx={sectionActionButtonSx}
          >
            Adjust Points
          </Button>
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          columnGap: 6,
          rowGap: 2,
          alignItems: 'start',
        }}
      >
        <Stack direction="column" gap={2}>
          {account.accountNumber ? (
            <Box>
              <Typography variant="h6">{LUX_LOYALTY_PROGRAM_CONFIG.programDisplayName} LE Member ID</Typography>
              <CopyableField variant="subtitle1" value={account.accountNumber} />
            </Box>
          ) : null}
          <Box>
            <Typography variant="h6">{LUX_LOYALTY_PROGRAM_CONFIG.programDisplayName} External Provider ID</Typography>
            <CopyableField variant="subtitle1" value={account.ibsMemberId} />
          </Box>
          <Stack direction="row" alignItems="center" gap={2}>
            <Box>
              <Typography variant="h6">Tier</Typography>
              <Typography variant="subtitle1">{getLuxLoyaltyTierDisplayName(account.tier)}</Typography>
            </Box>
            {canChangeTier && UPGRADEABLE_TIERS.includes(account.tier) && (
              <Button
                variant="contained"
                disableElevation
                onClick={() => setChangeTierModalOpen(true)}
                sx={sectionActionButtonSx}
              >
                Change Tier
              </Button>
            )}
          </Stack>
          <Box>
            <Typography variant="h6">Region</Typography>
            <Typography variant="subtitle1">{account.region}</Typography>
          </Box>
          <RegionSwitcher account={account} onRegionUpdated={onAccountUpdated} />
          {!!account.createdAt && (
            <Box>
              <Typography variant="h6">Account Created</Typography>
              <Typography variant="subtitle1">{formatDateSlashesWithClock(account.createdAt)}</Typography>
            </Box>
          )}
          {isInfinite && (
            <Stack direction="row" alignItems="center" gap={2}>
              <Box>
                <Typography variant="h6">Customer Service Rep</Typography>
                {linkedRep ? (
                  <Stack mt={0.5}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {linkedRep.firstName} {linkedRep.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {linkedRep.email} · {linkedRep.phone}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    No rep assigned yet.
                  </Typography>
                )}
              </Box>
              <Button
                variant="contained"
                disableElevation
                onClick={() => setChangeRepDialogOpen(true)}
                sx={sectionActionButtonSx}
              >
                {linkedRep ? 'Change rep' : 'Assign rep'}
              </Button>
              {linkedRep && (
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={
                    resendInfiniteOnboardingEmailMutation.isPending ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <EmailOutlinedIcon fontSize="small" />
                    )
                  }
                  disabled={resendInfiniteOnboardingEmailMutation.isPending}
                  onClick={() => resendInfiniteOnboardingEmailMutation.mutate({ customerId: account.customerId })}
                  sx={sectionActionButtonSx}
                >
                  Send onboarding email
                </Button>
              )}
            </Stack>
          )}
        </Stack>

        <Stack direction="column" gap={2}>
          {!!account.statusReviewDate && (
            <Box>
              <Typography variant="h6">Membership Renewal Date</Typography>
              <Typography variant="subtitle1">{formatDateSlashes(account.statusReviewDate)}</Typography>
            </Box>
          )}
          {!!account.totalCreditsForTierRetention && (
            <Box>
              <Typography variant="h6">{formattedStatusCreditsName} Required for Tier Retention</Typography>
              <Typography variant="subtitle1">{account.totalCreditsForTierRetention}</Typography>
            </Box>
          )}
          {!!account.totalCreditsForTierUpgrade && (
            <Box>
              <Typography variant="h6">{formattedStatusCreditsName} Required for Tier Upgrade</Typography>
              <Typography variant="subtitle1">{account.totalCreditsForTierUpgrade}</Typography>
            </Box>
          )}
          {!!account.numberOfNightsForTierRetention && (
            <Box>
              <Typography variant="h6">Number of Nights Required for Tier Retention</Typography>
              <Typography variant="subtitle1">{account.numberOfNightsForTierRetention}</Typography>
            </Box>
          )}
          {!!account.numberOfNightsForTierUpgrade && (
            <Box>
              <Typography variant="h6">Number of Nights Required for Tier Upgrade</Typography>
              <Typography variant="subtitle1">{account.numberOfNightsForTierUpgrade}</Typography>
            </Box>
          )}
        </Stack>
      </Box>

      <Divider sx={{ display: { md: 'none' } }} />

      <LuxLoyaltyChangeTierModal
        open={changeTierModalOpen}
        onClose={() => setChangeTierModalOpen(false)}
        customerId={account.customerId}
        currentTier={account.tier}
        onSuccess={() => onAccountUpdated?.()}
      />
      {isInfinite && (
        <LuxLoyaltyChangeRepDialog
          open={changeRepDialogOpen}
          onClose={() => setChangeRepDialogOpen(false)}
          customerId={account.customerId}
          currentRepId={linkedRep?.id ?? null}
          onSuccess={() => onAccountUpdated?.()}
        />
      )}
      {canAdjustPoints && (
        <LuxLoyaltyAdjustPointsModal
          open={adjustPointsModalOpen}
          onClose={() => setAdjustPointsModalOpen(false)}
          customerId={account.customerId}
          onSuccess={() => onAccountUpdated?.()}
        />
      )}
    </Stack>
  );
}
